import { useQueries } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { envelopeData } from '../../../core/network/envelope';
import { donorKeys } from '../queries/keys';
import { donorPaths } from '../queries/paths';
import { useClientDonors } from './donors';

// TSS vite/03 — portafolio de inversión del cliente. El contrato NO expone una
// serie AGREGADA (la `valorizacion.series` es POR donadora, en /donors/:id), así
// que para elegir la vaca PRINCIPAL (la que más creció) y su curva "tipo trading"
// se traen los detalles de las donadoras co-poseídas (?include=economics) con
// useQueries. Cap MAX para acotar el N (un cliente co-posee pocas vacas). Si el
// backend agrega un endpoint de serie de portafolio, se cambia solo este hook.

const MAX = 16;

export function useClientPortfolio() {
    const grid = useClientDonors({ view: 'summary', limit: 100 });
    const items = envelopeData(grid.data).items ?? [];
    const ids = items.slice(0, MAX).map((i) => i.id).filter((id) => id != null);

    const details = useQueries({
        queries: ids.map((id) => ({
            queryKey: donorKeys.detail(id, { inc: 'valz' }),
            queryFn: ({ signal }) =>
                api.get(donorPaths.detail(id), { params: { include: 'economics' }, signal }).then((r) => r.data),
            staleTime: 30_000,
        })),
    });

    const cows = ids.map((id, i) => {
        const item = items[i] || {};
        const d = envelopeData(details[i]?.data);
        const v = d.economics?.valorizacion;
        return {
            id,
            name: item.name || `Donadora #${id}`,
            number: item.number ?? null,
            percent: d.percent ?? item.percent ?? null,
            growthPct: v?.stake_pct ?? v?.pct ?? null,
            stakeAbs: v?.stake_abs ?? null,
            currentValue: d.stake_value ?? item.stake_value ?? null,
            series: Array.isArray(v?.series) ? v.series : [],
        };
    });

    const ranked = cows
        .filter((c) => c.growthPct != null)
        .sort((a, b) => b.growthPct - a.growthPct);

    // Principal para el gráfico: mejor crecimiento CON serie graficable (≥2 pts);
    // si ninguno tiene serie, el de mejor crecimiento; si no hay crecimiento aún,
    // el de mayor valor de participación.
    const principal =
        ranked.find((c) => c.series.length >= 2) ||
        ranked[0] ||
        [...cows].sort((a, b) => (b.currentValue || 0) - (a.currentValue || 0))[0] ||
        null;

    return {
        isLoading: grid.isLoading || details.some((q) => q.isLoading),
        isError: grid.isError,
        hasData: items.length > 0,
        count: items.length,
        cows,
        ranked,
        principal,
    };
}
